import { useEffect, useState } from "react";
import { Flex, View } from "@adobe/react-spectrum";
import ActiveCallContainer from "../../CallTab/CallDialer/ActiveCallContainer";
import CallHistorySidebar from "../../CallTab/CallHistory/CallHistorySidebar";
import CVHistorySidebar from "../CVHistorySidebar/CVHistorySidebar";
import HistoryDetail from "../HistoryDetail/HistoryDetail";
import { CALL, CALLBACK, VOICEMAIL } from "../../../constants/constants";
import ChannelHistoryFilter from "../ChannelHistoryFilter/ChannelHistoryFilter";

type HistoryItem = {
  id: string;
  self?: {
    missed?: boolean;
  };
};

interface HistoryContainerProps<T extends HistoryItem> {
  data: T[] | undefined;
  type: "callback" | "call" | "voicemail";
  setCount: (count: number) => void;
  emptyTitle: string;
  emptyDescription: string;
}

export function HistoryContainer<T extends HistoryItem>({
  data,
  type,
  setCount,
  emptyTitle,
  emptyDescription,
}: HistoryContainerProps<T>) {
  const [activeCallDetail, setActiveCallDetail] = useState<T | null>(null);
  const [callList, setCallList] = useState<T[]>([]);
  const [filterAction, setFilterAction] = useState<"all" | "missed">("all");

  useEffect(() => {
    if (data?.length) {
      setCallList(data);
      setActiveCallDetail(data[0]);
      setCount(data.length);
    }
  }, [data, setCount]);

  const getActiveCallId = (id: string) => {
    const found = data?.find((call) => call.id === id);
    if (found) setActiveCallDetail(found);
  };

  const updateList = (action: "all" | "missed", list: T[]) => {
    setFilterAction(action);
    setCallList(list);
    setActiveCallDetail(list[0] ?? null);
    setCount(list.length);
  };

  const handleMissed = () => {
    const missed = data?.filter((call) => call.self?.missed) ?? [];
    updateList("missed", missed);
  };

  const handleAll = () => {
    updateList("all", data ?? []);
  };

  const renderHistoryComponents = () => {
    if (!activeCallDetail || ![CALL, VOICEMAIL, CALLBACK].includes(type)) {
      return null;
    }

    const isCall = type === CALL;

    return (
      <>
        {isCall ? (
          <CallHistorySidebar
            activeCallerId={activeCallDetail.id}
            getActiveCallId={getActiveCallId}
            callList={callList}
          />
        ) : (
          <CVHistorySidebar
            activeCallerId={activeCallDetail.id}
            getActiveCallId={getActiveCallId}
            callList={callList}
            type={type}
          />
        )}
        <HistoryDetail activeCallDetail={activeCallDetail} type={type} />
      </>
    );
  };

  const noDataTitle =
    filterAction === "missed" ? `No missed ${type}s` : emptyTitle;
  const noDataDescription =
    filterAction === "missed"
      ? `No missed ${type}s available.`
      : emptyDescription;

  const hasData = (data?.length ?? 0) > 0;

  return (
    <View backgroundColor="static-white" width="100%" height="100%">
      <Flex direction="column" height="100%">
        {hasData && (
          <ChannelHistoryFilter
            activeFilter={filterAction}
            onAll={handleAll}
            onMissed={handleMissed}
          />
        )}
        <View width="100%" UNSAFE_className="call-history-container">
          {!callList.length ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              width="90%"
              height="100%"
            >
              <ActiveCallContainer
                title={noDataTitle}
                description={noDataDescription}
              />
            </Flex>
          ) : (
            <Flex direction="row" alignItems="start" height="100%" width="100%">
              {renderHistoryComponents()}
            </Flex>
          )}
        </View>
      </Flex>
    </View>
  );
}
